// Geometry + turn-by-turn helpers for in-app navigation. Coordinates are [lat, lng].
const R = 6371000;
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

export function distM(a, b) {
  const dLat = rad(b[0] - a[0]);
  const dLon = rad(b[1] - a[1]);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function bearing(a, b) {
  const φ1 = rad(a[0]), φ2 = rad(b[0]), Δλ = rad(b[1] - a[1]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

export function destPoint(a, brg, d) {
  const δ = d / R, θ = rad(brg), φ1 = rad(a[0]), λ1 = rad(a[1]);
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
  return [deg(φ2), deg(λ2)];
}

export function angleDiff(a, b) {
  let d = ((b - a + 540) % 360) - 180;
  return d;
}

// Local equirectangular projection (metres) around a reference lat for fast segment maths.
function toXY(p, refLat) {
  return [rad(p[1]) * R * Math.cos(rad(refLat)), rad(p[0]) * R];
}

// Nearest point on polyline. Returns { seg, point, distM, alongM } where alongM is the
// distance from the start of the path to the projected point.
export function projectOnPath(path, p) {
  if (!path || path.length === 0) return null;
  if (path.length === 1) return { seg: 0, point: path[0], distM: distM(path[0], p), alongM: 0 };
  const refLat = p[0];
  const P = toXY(p, refLat);
  let best = { distM: Infinity };
  let cum = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const A = toXY(path[i], refLat), B = toXY(path[i + 1], refLat);
    const abx = B[0] - A[0], aby = B[1] - A[1];
    const len2 = abx * abx + aby * aby || 1e-9;
    let t = ((P[0] - A[0]) * abx + (P[1] - A[1]) * aby) / len2;
    t = Math.max(0, Math.min(1, t));
    const qx = A[0] + t * abx, qy = A[1] + t * aby;
    const d = Math.hypot(P[0] - qx, P[1] - qy);
    const segLen = Math.sqrt(len2);
    if (d < best.distM) {
      const pt = [path[i][0] + t * (path[i + 1][0] - path[i][0]), path[i][1] + t * (path[i + 1][1] - path[i][1])];
      best = { seg: i, point: pt, distM: d, alongM: cum + t * segLen };
    }
    cum += segLen;
  }
  return best;
}

export function pathLength(path) {
  let m = 0;
  for (let i = 0; i < path.length - 1; i++) m += distM(path[i], path[i + 1]);
  return m;
}

// Douglas-Peucker simplification (tolerance in metres) so tiny wobbles don't become "turns".
function simplify(path, tol) {
  if (path.length < 3) return path;
  const refLat = path[0][0];
  const pts = path.map((p) => toXY(p, refLat));
  const keep = new Array(path.length).fill(false);
  keep[0] = keep[path.length - 1] = true;
  const stack = [[0, path.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop();
    let maxD = 0, idx = -1;
    const A = pts[s], B = pts[e];
    const abx = B[0] - A[0], aby = B[1] - A[1];
    const len = Math.hypot(abx, aby) || 1e-9;
    for (let i = s + 1; i < e; i++) {
      const d = Math.abs(abx * (A[1] - pts[i][1]) - (A[0] - pts[i][0]) * aby) / len;
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > tol && idx > 0) { keep[idx] = true; stack.push([s, idx], [idx, e]); }
  }
  return path.filter((_, i) => keep[i]);
}

const turnType = (a) => {
  const m = Math.abs(a);
  if (m < 25) return null;
  const side = a > 0 ? "right" : "left";
  if (m < 50) return `slight-${side}`;
  if (m < 130) return side;
  return `sharp-${side}`;
};

export const turnLabel = {
  "slight-left": "Bear left", left: "Turn left", "sharp-left": "Sharp left",
  "slight-right": "Bear right", right: "Turn right", "sharp-right": "Sharp right",
  straight: "Continue straight", arrive: "Arrive at",
};

// Build the maneuver list for a leg (array of [lat,lng]) ending at `stopName`.
export function buildManeuvers(leg, stopName) {
  if (!leg || leg.length < 2) return [];
  const simp = simplify(leg, 8);
  const total = pathLength(leg);
  const out = [];
  let cum = 0;
  for (let i = 1; i < simp.length - 1; i++) {
    cum += distM(simp[i - 1], simp[i]);
    const a = angleDiff(bearing(simp[i - 1], simp[i]), bearing(simp[i], simp[i + 1]));
    const t = turnType(a);
    if (t) out.push({ type: t, point: simp[i], atM: cum, label: turnLabel[t] });
  }
  out.push({ type: "arrive", point: leg[leg.length - 1], atM: total, label: `${turnLabel.arrive} ${stopName}` });
  return out;
}

// "___ metres" blanks in owner prompts → real remaining distance from the cue to the leg's end
export function fillBlanks(text, remainingM) {
  if (!text || !/_{2,}/.test(text)) return text;
  if (remainingM >= 1000) return text.replace(/_{2,}\s*(metres|meters|m)\b/i, `${(remainingM / 1000).toFixed(1)} km`).replace(/_{2,}/g, `${(remainingM / 1000).toFixed(1)} km`);
  return text.replace(/_{2,}/g, String(Math.max(10, Math.round(remainingM / 10) * 10)));
}

// Owner-plotted cues as the maneuver list: each cue is spoken verbatim at its GPS spot (no generated turns).
export function buildOwnerManeuvers(leg, cues, stopName) {
  if (!leg || leg.length < 2) return [];
  const total = pathLength(leg);
  const out = (cues || []).filter((c) => c.text && c.lat != null && c.lng != null).map((c) => {
    const pr = projectOnPath(leg, [c.lat, c.lng]);
    const t = /\bleft\b/i.test(c.text) ? "left" : /\bright\b/i.test(c.text) ? "right" : "straight";
    const atM = Math.min(pr.alongM, total);
    return { type: c.kind === "dir" ? t : "note", kind: c.kind, point: [c.lat, c.lng], atM, label: fillBlanks(c.text, total - atM), audio: c.audio || null, owner: true };
  }).sort((a, b) => a.atM - b.atM);
  out.push({ type: "arrive", point: leg[leg.length - 1], atM: total, label: `${turnLabel.arrive} ${stopName}` });
  return out;
}

export function fmtM(m) {
  if (m == null || isNaN(m)) return "";
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  if (m >= 100) return `${Math.round(m / 10) * 10} m`;
  return `${Math.max(0, Math.round(m / 5) * 5)} m`;
}

export function etaText(m, kmh = 14) {
  const mins = Math.round((m / 1000 / kmh) * 60);
  if (mins < 1) return "under a minute";
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// Current instruction for a rider located at alongM along the leg.
export function currentInstruction(maneuvers, alongM) {
  if (!maneuvers.length) return null;
  const next = maneuvers.find((mv) => mv.atM - alongM > -12) || maneuvers[maneuvers.length - 1];
  const dist = Math.max(0, next.atM - alongM);
  const idx = maneuvers.indexOf(next);
  return { ...next, distM: dist, then: maneuvers[idx + 1] || null };
}

// Browser voice prompts
let muted = false;
export const setVoiceMuted = (v) => { muted = v; if (v && window.speechSynthesis) window.speechSynthesis.cancel(); };
export function speak(text) {
  if (muted || !window.speechSynthesis || !text) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-NZ";
    u.rate = 1;
    window.speechSynthesis.speak(u);
  } catch { /* unsupported */ }
}
